///
/// EarthBrowser
///
/// Created by Matt Giger on 9/12/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Screen-space labels for vector tile point layers: division names
/// (countries/regions) and road route shields. Follows the
/// SatelliteLabelsOverlay pattern: canvas-textured panes drawn in the
/// depth-preserving label pass so the globe occludes labels on its far side.
///

import { type RenderActionSet } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { rgba } from "@stovokor/math/color"
import { type StovokorContext } from "@stovokor/StovokorContext"
import { Canvas } from "@stovokor/uipanes/Canvas"
import { Font, FontFamily } from "@stovokor/uipanes/Font"
import { bind, DepthPaneRenderer, Pane } from "@stovokor/uipanes/Pane"

export type MapLabelKind = "country" | "region" | "shield"

/** What a label extractor produces from one point feature's properties */
export interface MapLabelSpec {
	/** Stable identity, e.g. "country:FR" or "shield:US:I:90" */
	id: string
	text: string
	kind: MapLabelKind
	/** Route network for shields, e.g. "US:I" (drives fallback shield styling) */
	network?: string
	/** Official shield artwork name in the shield atlas, e.g. "I-5_(OR).svg" */
	symbol?: string
	/** Higher wins during decluttering */
	priority: number
	/** Hide above this camera altitude (km) */
	maxAltitudeKm: number
	/** Approximate extents for decluttering (px) */
	widthPx: number
	heightPx: number
	/** Suffix the id with the point's rounded position (multi-placement labels) */
	dedupeByPosition?: boolean
	/**
	 * Id of the label whose geographic extent this point contributes to
	 * (a region point extends its country). A label with a known extent
	 * fades out once that extent outgrows the viewport
	 */
	extendsLabel?: string
}

/** A placed label, ready for the overlay */
export interface MapLabelData {
	id: string
	text: string
	kind: MapLabelKind
	network?: string
	symbol?: string
	screenX: number
	screenY: number
	/** Perspective NDC depth [0,1] for gl_FragDepth occlusion by the globe */
	worldDepth: number
	alpha: number
}

// Division typography: countries are tracked uppercase in the same warm bone
// white as their boundary lines, regions smaller in the cool blue-grey of
// theirs, so each reads as the name of its line and neither competes with
// the pure-white city labels
const cCountryFont = new Font(FontFamily.sansserif, 12, "bold")
const cRegionFont = new Font(FontFamily.sansserif, 11, "normal")
const cShieldFont = new Font(FontFamily.sansserif, 10, "bold")
const cCountryColor = rgba(1.0, 0.96, 0.88, 0.85)
const cRegionColor = rgba(0.80, 0.86, 0.94, 0.78)
// Letter spacing (px) added between glyphs
const cCountryTracking = 1.8
const cRegionTracking = 0.4
// Text outline: slightly softer than the city labels' so it recedes
const cCountryOutlinePx = 3
const cRegionOutlinePx = 2.5
const cOutlineAlpha = 0.7
const cFadeSpeed = 6 // exponential fade rate (1/s)

/** Width of text drawn glyph by glyph with the given tracking */
function trackedWidth(context: OffscreenCanvasRenderingContext2D, text: string, tracking: number): number {
	let width = 0
	for (const glyph of text) {
		width += context.measureText(glyph).width
	}
	return width + Math.max(0, text.length - 1) * tracking
}

/**
 * Draw text glyph by glyph with the given tracking. All outlines are
 * stroked before any fill so a glyph's outline never cuts into its neighbor
 */
function drawTrackedText(context: OffscreenCanvasRenderingContext2D, text: string, x: number, y: number, tracking: number, stroke: boolean): void {
	const advances: number[] = []
	let penX = x
	for (const glyph of text) {
		advances.push(penX)
		penX += context.measureText(glyph).width + tracking
	}
	const glyphs = Array.from(text)
	if (stroke) {
		glyphs.forEach((glyph, i) => context.strokeText(glyph, advances[i], y))
	}
	glyphs.forEach((glyph, i) => context.fillText(glyph, advances[i], y))
}

interface ShieldAtlasEntry {
	x: number
	y: number
	w: number
	h: number
	/** Blank sign artwork: the route ref is drawn on top */
	blank?: boolean
}

/**
 * Sprite atlas of official route-shield artwork (built by
 * tools/vector-tiles/build-shield-atlas.ts, shipped as app assets). Sprites
 * are 2x resolution; display size is w/2 x h/2.
 */
class ShieldAtlas {
	public static readonly shared = new ShieldAtlas()

	public image?: HTMLImageElement
	public onLoad?: () => void

	private entries: Record<string, ShieldAtlasEntry> = {}
	private ready = false
	private loading = false

	public entry(symbol: string): ShieldAtlasEntry | undefined {
		if (!this.loading) {
			this.load()
		}
		return this.ready ? this.entries[symbol] : undefined
	}

	private load(): void {
		this.loading = true
		fetch("/shields/shield-atlas.json")
			.then((response) => response.json())
			.then((entries: Record<string, ShieldAtlasEntry>) => {
				const image = new Image()
				image.onload = () => {
					this.entries = entries
					this.image = image
					this.ready = true
					this.onLoad?.()
				}
				image.src = "/shields/shield-atlas.png"
			})
			.catch(() => {}) // fall back to drawn shields
	}
}

interface ShieldStyle {
	fill: string
	border: string
	text: string
}

/** Shield colors keyed by route network prefix */
function shieldStyle(network: string): ShieldStyle {
	if (network.startsWith("US:I")) {
		// Interstate: blue field, white border
		return { fill: "rgba(23, 64, 145, 0.92)", border: "rgba(255, 255, 255, 0.9)", text: "rgba(255, 255, 255, 0.95)" }
	}
	if (network.startsWith("US:US")) {
		// US route: white field, black text
		return { fill: "rgba(240, 240, 240, 0.92)", border: "rgba(30, 30, 30, 0.9)", text: "rgba(20, 20, 20, 0.95)" }
	}
	// State routes and everything else: dark slate
	return { fill: "rgba(52, 58, 66, 0.92)", border: "rgba(220, 220, 220, 0.85)", text: "rgba(255, 255, 255, 0.95)" }
}

/**
 * Canvas that paints either a text label (with dark outline) or a route shield
 * (rounded rect with the route number)
 */
class MapLabelCanvas extends Canvas {
	private text: string
	private kind: MapLabelKind
	private network: string
	private symbol: string
	private alphaValue = 1

	constructor(text: string, kind: MapLabelKind, network: string, symbol: string) {
		super()
		this.text = kind === "country" ? text.toUpperCase() : text
		this.kind = kind
		this.network = network
		this.symbol = symbol
	}

	public set alpha(value: number) {
		if (Math.abs(value - this.alphaValue) > 0.02) {
			this.alphaValue = value
			this._needsUpdate = true
		}
	}

	/** Repaint (used when the shield atlas finishes loading) */
	public refresh(): void {
		this._needsUpdate = true
	}

	private atlasEntry(): ShieldAtlasEntry | undefined {
		return this.symbol ? ShieldAtlas.shared.entry(this.symbol) : undefined
	}

	override intrinsicSize(): [number, number] {
		if (this.text.length === 0) {
			return [this.width, this.height]
		}
		const context = this.context
		if (this.kind === "shield") {
			const entry = this.atlasEntry()
			if (entry) {
				return [Math.ceil(entry.w / 2), Math.ceil(entry.h / 2)]
			}
			context.font = cShieldFont.toString()
			const metrics = context.measureText(this.text)
			return [Math.max(26, Math.ceil(metrics.width) + 14), 19]
		}
		const country = this.kind === "country"
		context.font = (country ? cCountryFont : cRegionFont).toString()
		const metrics = context.measureText(this.text)
		const width = Math.ceil(trackedWidth(context, this.text, country ? cCountryTracking : cRegionTracking)) + 10
		const height = Math.ceil(metrics.fontBoundingBoxAscent + metrics.fontBoundingBoxDescent) + 8
		return [width, height]
	}

	override render(): void {
		if (this.text.length === 0) {
			return
		}
		if (this.kind === "shield") {
			this.renderShield()
		} else {
			this.renderText()
		}
	}

	private renderText(): void {
		const context = this.context
		const country = this.kind === "country"
		const font = country ? cCountryFont : cRegionFont
		const baseColor = country ? cCountryColor : cRegionColor
		const tracking = country ? cCountryTracking : cRegionTracking
		context.textAlign = "left"
		context.textBaseline = "top"
		context.font = font.toString()

		const x = (this.width - trackedWidth(context, this.text, tracking)) / 2
		const y = 4
		const alpha = baseColor.a * this.alphaValue

		context.strokeStyle = `rgba(0, 0, 0, ${cOutlineAlpha * alpha})`
		context.lineWidth = country ? cCountryOutlinePx : cRegionOutlinePx
		context.lineJoin = "round"
		context.miterLimit = 2
		context.fillStyle = rgba(baseColor.r, baseColor.g, baseColor.b, alpha).toString()
		drawTrackedText(context, this.text, x, y, tracking, true)
	}

	private renderShield(): void {
		const context = this.context
		const w = this.width
		const h = this.height

		// Official artwork from the shield atlas when available
		const entry = this.atlasEntry()
		const atlasImage = ShieldAtlas.shared.image
		if (entry && atlasImage) {
			const dw = entry.w / 2
			const dh = entry.h / 2
			const dx = (w - dw) / 2
			const dy = (h - dh) / 2
			context.globalAlpha = this.alphaValue
			context.drawImage(atlasImage, entry.x, entry.y, entry.w, entry.h, dx, dy, dw, dh)
			if (entry.blank) {
				// Blank sign artwork: paint the route number on it
				context.font = cShieldFont.toString()
				context.textAlign = "center"
				context.textBaseline = "middle"
				context.fillStyle = "rgba(20, 20, 20, 0.95)"
				context.fillText(this.text, w / 2, h / 2 + 0.5)
			}
			context.globalAlpha = 1
			return
		}

		const style = shieldStyle(this.network)
		const radius = 4

		context.globalAlpha = this.alphaValue
		context.beginPath()
		context.roundRect(1, 1, w - 2, h - 2, radius)
		context.fillStyle = style.fill
		context.fill()
		context.lineWidth = 1.5
		context.strokeStyle = style.border
		context.stroke()

		context.font = cShieldFont.toString()
		context.textAlign = "center"
		context.textBaseline = "middle"
		context.fillStyle = style.text
		context.fillText(this.text, w / 2, h / 2 + 0.5)
		context.globalAlpha = 1
	}
}

/**
 * A single map label
 */
class MapLabel extends Pane {
	public targetAlpha = 0
	public currentAlpha = 0
	public screenX = 0
	public screenY = 0

	private textCanvas: MapLabelCanvas
	private depthRenderer: DepthPaneRenderer

	constructor(context: StovokorContext, text: string, kind: MapLabelKind, network: string, symbol: string) {
		super(context)

		this.depthRenderer = new DepthPaneRenderer(context, this)
		this.renderer = this.depthRenderer

		// Start tiny: Canvas.rerender only grows to intrinsic size, so a large
		// initial pane would keep its width (visible on shields, whose rect
		// background fills the whole pane)
		this.size(2, 2)
		this.adoptIntrinsicSize = true
		this.textCanvas = new MapLabelCanvas(text, kind, network, symbol)
		this._contents = this.textCanvas
	}

	public set worldDepth(value: number) {
		this.depthRenderer.worldDepth.value = value
	}

	public set alpha(value: number) {
		this.textCanvas.alpha = value
	}

	/** Repaint contents (used when the shield atlas finishes loading) */
	public refresh(): void {
		this.textCanvas.refresh()
	}
}

/**
 * Overlay pane managing map labels, pooled by id
 */
export class MapLabelsOverlay extends Pane {
	private labels = new Map<string, MapLabel>()
	private lastFrameTime = 0

	constructor(context: StovokorContext) {
		super(context)
		this.bind(bind.width | bind.height)
		this.transparency = true

		// Repaint pooled shields once official artwork becomes available
		ShieldAtlas.shared.onLoad = () => {
			for (const [, label] of this.labels) {
				label.refresh()
			}
		}
	}

	/**
	 * Update label positions/visibility. Called each frame by the layer;
	 * labels absent from the list fade out and hide.
	 */
	public updateLabels(data: MapLabelData[]): void {
		const seen = new Set<string>()

		for (const entry of data) {
			seen.add(entry.id)
			let label = this.labels.get(entry.id)
			if (!label) {
				label = new MapLabel(this.context, entry.text, entry.kind, entry.network ?? "", entry.symbol ?? "")
				label.visible = false
				this.labels.set(entry.id, label)
				this.addNode(label)
			}
			label.targetAlpha = entry.alpha
			label.screenX = entry.screenX
			label.screenY = entry.screenY
			label.worldDepth = entry.worldDepth
		}

		for (const [id, label] of this.labels) {
			if (!seen.has(id)) {
				label.targetAlpha = 0
			}
		}
	}

	/**
	 * Animate alphas and position labels each frame
	 */
	override render(state: RenderState): RenderActionSet[] {
		const now = performance.now()
		const dt = this.lastFrameTime > 0 ? Math.min((now - this.lastFrameTime) / 1000, 0.1) : 1 / 60
		this.lastFrameTime = now

		const blend = Math.min(1, dt * cFadeSpeed)
		for (const [, label] of this.labels) {
			label.currentAlpha += (label.targetAlpha - label.currentAlpha) * blend

			if (label.currentAlpha > 0.02) {
				label.visible = true
				label.alpha = label.currentAlpha
				// Center on the point
				label.offset(label.screenX - label.width / 2, label.screenY - label.height / 2)
			} else {
				label.visible = false
			}
		}

		return super.render(state)
	}
}

/** Extractor for the borders archive's division_labels layer */
export function extractDivisionLabel(props: Record<string, unknown>): MapLabelSpec | undefined {
	const name = typeof props.name === "string" ? props.name : undefined
	const subtype = props.subtype === "country" || props.subtype === "region" ? props.subtype : undefined
	if (!name || !subtype) {
		return undefined
	}

	// Stable identity across tiles/levels via ISO codes when present
	const isoCode = subtype === "region" ? props.region : props.country
	const code = typeof isoCode === "string" ? isoCode : name
	const population = typeof props.population === "number" ? props.population : 0
	const country = subtype === "country"
	const fontSize = country ? cCountryFont.size : cRegionFont.size
	// Uppercase bold glyphs average ~0.7 em, mixed case ~0.58 em, plus tracking
	const glyphPx = country ? fontSize * 0.7 + cCountryTracking : fontSize * 0.58 + cRegionTracking

	// Region points sketch their country's extent as tiles load
	const countryCode = typeof props.country === "string" ? props.country : undefined

	return {
		id: `${subtype}:${code}`,
		text: name,
		kind: subtype,
		priority: (country ? 1e12 : 0) + population,
		maxAltitudeKm: country ? Infinity : 3500,
		widthPx: name.length * glyphPx + 12,
		heightPx: fontSize * 1.4 + 8,
		extendsLabel: !country && countryCode ? `country:${countryCode}` : undefined
	}
}

/** Extractor for the roads archive's road_shields layer */
export function extractRoadShield(props: Record<string, unknown>): MapLabelSpec | undefined {
	const ref = typeof props.ref === "string" ? props.ref : undefined
	if (!ref) {
		return undefined
	}
	const network = typeof props.network === "string" ? props.network : ""
	const symbol = typeof props.symbol === "string" && props.symbol.length > 0 ? props.symbol : undefined

	// Interstates read from farther out than US routes and state routes
	let tier = 1
	let maxAltitudeKm = 150
	if (network.startsWith("US:I")) {
		tier = 3
		maxAltitudeKm = 500
	} else if (network.startsWith("US:US")) {
		tier = 2
		maxAltitudeKm = 250
	}

	// Extents from the official artwork when the atlas has it
	const atlasEntry = symbol ? ShieldAtlas.shared.entry(symbol) : undefined
	const widthPx = atlasEntry ? Math.ceil(atlasEntry.w / 2) : Math.max(26, ref.length * 7 + 14)
	const heightPx = atlasEntry ? Math.ceil(atlasEntry.h / 2) : 19

	return {
		id: `shield:${network}:${ref}`,
		text: ref,
		kind: "shield",
		network,
		symbol,
		priority: tier,
		maxAltitudeKm,
		widthPx,
		heightPx,
		dedupeByPosition: true
	}
}
